import {
    all, call, fork, put, select, takeLatest,
} from 'redux-saga/effects';
import { LOCATION_CHANGE, LocationChangeAction, RouterState } from 'connected-react-router';
import { LocationState } from 'history';
import * as Paho from 'paho-mqtt';

import commonWorkspaceSchemeService from 'common/services/commonWorkspaceSchemeService';
import { closeWorkspaceOverlay, workspaceSchemeLoaded } from 'common/store/workspaces/actions';
import { WorkspacesState } from 'common/store/workspaces/reducer';
import { Alert, AlertLevel } from 'common/components/notifications/types';
import { pushAlert } from 'common/components/notifications/actions';
import commonApiService from 'common/services/api/commonApiService';
import { WorkspaceGroup, WorkspaceItem, InternalReferenceWorkspaceItem } from 'common/store/common/workspaceScheme/types';
import { setMarketplaceProperties, setUserInfo } from 'common/store/session/actions';
import { SessionState, UserObject } from 'common/store/session/reducer';
import { propsAreIdentical } from 'common/util/object/objectUtil';
import { isZeroOrPositiveInt } from 'common/util/type/typeUtil';

import urlUtil from 'common/util/url/urlUtil';
import i18nService, { tl, ContentLanguagesDefinition } from 'common/services/i18nService';
import {
    categories, CONTENT_ITEM_TEMPLATES, ContentTypes, qtiTypes,
} from 'common/store/common/constants';
import { isQuestionSetSummaryWorkspace } from 'common/util/shared/sharedUtils';
import {
    hasCURAccessForAction,
    hasAuthorAccessViaCUR,
    hasQuestionTestCasesAccess,
    isPrivateContentSharedWithCurrentUser,
} from 'common/services/permissions/commonPermissionService';
import { FeatureTypesWithCurs } from 'common/services/permissions/typings';
import { ContextState } from './reducer';
import { fetchStructureEditorLiveData } from '../../features/structureEditor/actions';
import { watchQuestionSetSupportedLanguages } from './questionSetLanguageSagas';

import {
    AdditionalResource, ContextTypeKeys, ContextUrlSearchStringObject, navigableResourceType,
} from './types';
import routingService from '../../services/routingService';
import {
    RenameContextItem,
    resetContextEditStatus,
    resetQuestionIdAndScorableItemAndSectionId,
    restoreCurrentQtiContextItem,
    setContentItemIds,
    setContextEditStatus,
    setCurrentItemContext,
    setCurrentItemContextLock,
    setCurrentQtiContextItem,
    setCurrentResourceContextItem,
    setItemBankId,
    setItemBankInfo,
    setQuestionIdAndScorableItemAndSectionId,
    setupWorkspaces,
    setWorkspaceId,
    showContextLockModal,
    UnLockContext,
    workspaceIdUpdated,
    updateCurrentItemContext as updateItemContext,
    updateCurrentQtiContextItem as updateQtiContextItem,
    setIsIBTemplate,
    FetchAdditionalContextItemData,
    setContextQtiItemPublishStatus,
    setContextQtiItemBankId,
    FetchContextItemBankInfo,
    setContextItemBankInfoData,
    setItemBankForbidden,
    GetAssessmentAction,
    getAssessmentSuccess,
    UpdateAssessmentAction,
    getAssessment,
    updateAssessmentSuccess,
    getAssessmentDetailsSuccess,
    AddContentBlockSelectionAction,
    addContentBlockSelectionSuccess,
    BulkUpdateAssessmentAction,
    showBulkUpdateSuccessNotification,
    UpdateContentInBulkResponse,
    setActiveLanguage,
} from './actions';
import { RootState } from '../rootReducer';
import workspaceSchemeLoadingService from '../../services/workspaceSchemeLoadingService';
import permissionService from '../../services/permissions/PermissionService';
import urlContextParametersService from '../../services/urlContextParametersService';
import { ITEM_INDEX, QTI_CONTEXT_INDEX } from '../common';
import listLiveDataObjectLoaderService from '../../services/search/listLiveDataObjectLoaderService';
import apiService, { safe } from '../../services/apiService';
import { pickQuestionFromSection } from '../../services/sectionQuestionResolver';
import navigationListService from '../../services/navigationListService';
import awsIOTService from '../../services/AWSIOTService';
import storeManager from '..';
import { showNotification } from '../notification/actions';
import newAuthorUIService from '../../services/newAuthorUIService';
import { QUESTION_DELIVERY_TYPE } from '../../features/questionDelivery/types';

export const getContextState = (state: RootState) => state.context;
export const getRouterState = (state: RootState) => state.router;
export const getWorkspaceState = (state: RootState) => state.workspaces;
export const getSessionState = (state: RootState) => state.session;

const CONTEXT_LOAD_RETRY_LIMIT = 8;
let contextLoadRetryCount = 0;
let lastContextId: number | undefined;

const resolveWorkspaceIdForCURview = (workspaceId?: string): string | undefined => {
    if (
        (hasAuthorAccessViaCUR() || isPrivateContentSharedWithCurrentUser())
        && !hasCURAccessForAction(FeatureTypesWithCurs.CONTENT_BLOCK_EDIT)
        && (
            workspaceId === 'author_edit'
            || workspaceId?.startsWith('author_question_edit_only')
        )
    ) {
        return 'author_preview';
    }

    return workspaceId;
};

const isContextNotInSyncWithUrl = (contextState: ContextState, newContext: ContextUrlSearchStringObject) => {
    return !propsAreIdentical(
        [
            'contextId',
            'questionId',
            'scorableItem',
            'sectionId',
            'contentItemIds',
        ],
        contextState,
        newContext,
    );
};

function* getCurrentItemContext(oldContext: ContextState, newContext: ContextUrlSearchStringObject) {
    if (newContext.contextId && oldContext.contextId !== newContext.contextId) {
        return yield call(
            listLiveDataObjectLoaderService.getContextDataObject,
            ITEM_INDEX,
            newContext.contextId,
            -1,
            newContext.itemBankId
        );
    }
    return undefined;
}

function* fetchContextQtiData(action: FetchAdditionalContextItemData) {
    const { objectId, itemBankId } = action;
    const data = yield call(
        listLiveDataObjectLoaderService.getContextDataObject,
        ITEM_INDEX,
        0,
        objectId,
        itemBankId,
        true
    );
    const { publishStatus, itemBankId: contextQtiItemBankId } = data;
    yield put(setContextQtiItemPublishStatus(publishStatus));
    yield put(setContextQtiItemBankId(contextQtiItemBankId));
}

function* getCurrentQtiContextItem(oldContext: ContextState, newContext: ContextUrlSearchStringObject) {
    if (newContext.contextId && newContext.qtiContextItemId && oldContext.qtiContextItemId !== newContext.qtiContextItemId) {
        return yield call(
            listLiveDataObjectLoaderService.getContextDataObject,
            QTI_CONTEXT_INDEX,
            newContext.contextId,
            newContext.qtiContextItemId,
            newContext.itemBankId
        );
    }
    if (
        newContext.contextId
        && newContext.contextId !== -1
        && oldContext.contextId === newContext.contextId
        && oldContext.qtiContextItemId
        && oldContext.qtiContextItemId !== -1
        && oldContext.qtiContextItemId === newContext.qtiContextItemId
    ) {
        return oldContext.currentQtiContextItem;
    }
    return undefined;
}

export function* fetchContextItemBankInfoData(action: FetchContextItemBankInfo) {
    const { itemBankId } = action;
    const data = yield call(getItemBankInfo, {} as ContextState, {} as ContextUrlSearchStringObject, itemBankId);

    yield put(setContextItemBankInfoData(data));
}

function* getItemBankInfo(oldContext: ContextState, newContext: ContextUrlSearchStringObject, contextItemBankId?: number) {
    const canUpdate = newContext.itemBankId && oldContext.itemBankId !== newContext.itemBankId;
    if (contextItemBankId || canUpdate) {
        try {
            const itemBankId = newContext.itemBankId || contextItemBankId;
            const {
                itemBankName,
                statuses,
                itemStatsConfigurationPresetContent,
                itemStatsConfigurationPresetId,
                itemBankPresetStatusTransitionId,
                itemBankPresetStatusTransitionContent,
                customUserRoles,
                itemBankOrgUnits,
                itemBankId: apiItemBankId,
            } = yield call(apiService.getItemBankInfo, itemBankId);

            // clear any previous forbidden flag when successfully fetching info
            yield put(setItemBankForbidden(false));
            if (statuses && statuses.every((status: any) => status.contentStatus !== 'draft')) {
                statuses.push({
                    contentStatus: 'draft', lifeCycleStatus: '', lockEditContent: false, lockEditMetadata: false,
                });
            }
            return {
                itemBankId: apiItemBankId ?? itemBankId,
                itemBankName,
                statuses,
                itemStatsConfigurationPresetContent,
                itemStatsConfigurationPresetId,
                itemBankPresetStatusTransitionId,
                itemBankPresetStatusTransitionContent,
                customUserRoles,
                itemBankOrgUnits,
            };
        } catch (e) {
            const isForbidden = e && (e.status === 403 || e.response?.status === 403 || e.statusCode === 403);
            if (isForbidden) {
                // mark flag so App can render dedicated blank screen with alert
                yield put(setItemBankForbidden(true));
                // prevent other content from loading
                const alert: Alert = {
                    key: 'author.contentQti.key.permissionDenied',
                    info: 'author.contentQti.info.permissionDenied',
                    level: AlertLevel.ERROR,
                    payload: {},
                };
                yield put(pushAlert(alert, true));
            }
            throw e;
        }
    }
    return undefined;
}

function* updateWorkspaceId(oldContext: ContextState, newContext: ContextUrlSearchStringObject) {
    if ((oldContext.workspaceId !== newContext.workspaceId) && newContext.workspaceId) {
        yield put(setWorkspaceId(newContext.workspaceId));
        yield put(workspaceIdUpdated());
    }
}

function* updateItemBankId(oldContext: ContextState, newContext: ContextUrlSearchStringObject) {
    if ((oldContext.itemBankId !== newContext.itemBankId) && newContext.itemBankId) {
        sessionStorage.setItem('currentlySelectedItemBankId', String(newContext?.itemBankId));
        yield put(setItemBankId(newContext.itemBankId));
    }
}

function* updateIsIBTemplate(oldContext: ContextState, newContext: ContextUrlSearchStringObject) {
    if (newContext.contentTemplate === CONTENT_ITEM_TEMPLATES && newContext.contentTemplate !== oldContext.contentTemplate) {
        yield put(setIsIBTemplate(true));
    }
}

// manually resolve missing qtiContextItemId for item_review_xtm without blocking.
let cachedXtmSectionData: { contextId: number; data: any } | undefined;

function* resolveXtmQuestionContext(newContext: ContextUrlSearchStringObject, oldContext: ContextState): any {
    try {
        // Cache the section's question list per contextId so navigating between questions within
        // the same section doesn't re-fetch the whole section every time.
        let sectionData = cachedXtmSectionData?.contextId === newContext.contextId ? cachedXtmSectionData.data : undefined;
        if (!sectionData) {
            sectionData = yield call(apiService.fetchGetSection, newContext.contextId);
            cachedXtmSectionData = { contextId: newContext.contextId, data: sectionData };
        }

        const resolvedQuestion = pickQuestionFromSection(sectionData, newContext.scorableItem);

        if (resolvedQuestion?.contentItemId && resolvedQuestion.contentItemId > 0) {
            const resolvedQtiContext = yield call(
                listLiveDataObjectLoaderService.getContextDataObject,
                QTI_CONTEXT_INDEX,
                newContext.contextId,
                resolvedQuestion.contentItemId,
                newContext.itemBankId
            );

            if (resolvedQtiContext) {
                yield put(setCurrentQtiContextItem(resolvedQtiContext.objectId, resolvedQtiContext, oldContext.currentQtiContextItem));
            }
        }
    } catch (error) {
        console.error('Error resolving Cross Test Metrics question context:', error);
    }
}

function* updateContext(oldContext: ContextState, newContext: ContextUrlSearchStringObject, isFirstRendering: boolean): any {
    const context: ContextState = yield select(getContextState);
    const { isIBTemplate } = context;

    const itemContext = yield call(getCurrentItemContext, oldContext, newContext);

    // reset counter if contextId changes
    if (lastContextId !== newContext.contextId) {
        contextLoadRetryCount = 0;
        lastContextId = newContext.contextId;
    }

    // increment counter only when we expect data but don’t get it
    if (newContext.contextId && !itemContext && !isFirstRendering && (oldContext.contextId !== newContext.contextId || !context.currentItemContext)) {
        contextLoadRetryCount += 1;
    }
    if (contextLoadRetryCount >= CONTEXT_LOAD_RETRY_LIMIT) {
        // stop further retries
        contextLoadRetryCount = 0;
        yield put(setItemBankForbidden(true));
        const alert: Alert = {
            key: 'author.contentQti.key.permissionDenied',
            info: 'author.contentQti.info.permissionDenied',
            level: AlertLevel.ERROR,
            payload: {},
        };

        yield put(pushAlert(alert, true));
        return;
    }

    newContext.itemBankId = newContext.itemBankId || itemContext?.itemBankId || context?.currentItemContext?.itemBankId;

    if (isContextNotInSyncWithUrl(oldContext, newContext)) {
        if (!isZeroOrPositiveInt(newContext.contextId)) {
            if (Array.isArray(newContext.contentItemIds)) {
                newContext.contextId = newContext.contentItemIds[0];
            } else if (isZeroOrPositiveInt(newContext.contentItemIds)) {
                newContext.contextId = newContext.contentItemIds;
            }
        }
        if (oldContext.scorableItem && !newContext.scorableItem && oldContext.sectionId && !newContext.sectionId) {
            yield put(resetQuestionIdAndScorableItemAndSectionId());
        } else {
            yield put(setQuestionIdAndScorableItemAndSectionId(newContext.questionId, newContext.scorableItem, newContext.sectionId));
        }
        yield put(setContentItemIds(newContext.contentItemIds));
    }

    const [qtiContext, itemBankInfo] = yield all([
        call(getCurrentQtiContextItem, oldContext, newContext),
        call(getItemBankInfo, oldContext, newContext),
    ]);

    if (itemContext) {
        // For newly created question sets with empty supportedLanguages,
        // set default to marketplace's highest priority language (new author 2025 only)
        const shouldInitializeDefaultLanguage = newAuthorUIService.isNewAuthorUIEnabled() &&
            itemContext.contentType === ContentTypes.QUESTION_SET &&
            !itemContext.supportedLanguages?.length;

        if (shouldInitializeDefaultLanguage) {
            const defaultLanguage = i18nService.getFallbackLanguage();
            itemContext.supportedLanguages = [defaultLanguage as ContentLanguagesDefinition];
            yield put(setActiveLanguage(defaultLanguage as ContentLanguagesDefinition));
        }

        yield put(setCurrentItemContext(itemContext.objectId, itemContext));

        const hasItemReviewAccess = hasCURAccessForAction(FeatureTypesWithCurs.ITEM_REVIEW_ADDITIONAL_RESOURCES)
            || hasCURAccessForAction(FeatureTypesWithCurs.ITEM_REVIEW_METADATA)
            || hasCURAccessForAction(FeatureTypesWithCurs.ITEM_REVIEW_STATES)
            || hasCURAccessForAction(FeatureTypesWithCurs.ITEM_REVIEW_ASSOCIATED_CALIBRATION_TEST_EVENTS);

        const routerState: RouterState = yield select(getRouterState);
        const workspaceIdInUrl: string | undefined = urlUtil.getUrlParam('workspaceId') || urlUtil.getWorkspaceIdFromPathname(routerState);

        // The landing workspace is picked before the item is loaded, so a read-only share on private
        // content is only known here: move the user from an Edit workspace to Preview.
        const workspaceIdForCURview = resolveWorkspaceIdForCURview(workspaceIdInUrl);
        if (workspaceIdForCURview && workspaceIdForCURview !== workspaceIdInUrl) {
            navigationListService.navigateToWorkspace(workspaceIdForCURview);
        }

        const restrictedWorkspaces = new Set([
            'item_bank_default',
            'item_review_xtm',
        ]);

        if (!hasItemReviewAccess && restrictedWorkspaces.has(workspaceIdInUrl)) {
            const sessionState: SessionState = yield select(getSessionState);
            const workspacesState: WorkspacesState = yield select(getWorkspaceState);

            if (workspacesState.workspaceScheme) {
                let contributorPriority = getContributorPriority(
                    sessionState,
                    workspacesState,
                );

                // Remove only the default Item Review workspace
                contributorPriority = contributorPriority.filter(
                    (workspace) => !restrictedWorkspaces.has(workspace)
                );

                let selectedWorkspace = getLandingWorkspaceByPriority(
                    workspacesState.workspaceScheme.workspaceGroups,
                    contributorPriority,
                );

                if (!selectedWorkspace) {
                    selectedWorkspace = commonWorkspaceSchemeService.getFirstVisibleWorkspace(
                        workspacesState.workspaceScheme.workspaceGroups,
                    );
                }

                if (selectedWorkspace) {
                    const targetWorkspaceId = commonWorkspaceSchemeService.getWorkspaceIdentifier(selectedWorkspace);
                    const resolvedWorkspaceId = resolveWorkspaceIdForCURview(targetWorkspaceId);
                    navigationListService.navigateToWorkspace(resolvedWorkspaceId ?? targetWorkspaceId);
                }
            }
        }

        const { objectId, customPermissions } = itemContext;
        if (customPermissions && window.marketplaceProperties?.ENABLE_ORG_UNIT_WITH_CUR_ON_ITEM_BANK) {
            for (const permission of customPermissions) {
                const { granteeIds } = permission;
                for (const userId of granteeIds || []) {
                    const data = {
                        contentItemId: objectId,
                        customUserRoleId: permission?.customUserRoleId,
                        userId,
                    };
                    // This is a write, so read-only users get a 403. An uncaught error here aborts
                    // the saga tree and the app stops responding, so keep it contained.
                    try {
                        yield call(commonApiService.updateUserRole, data);
                    } catch (error) {
                        console.warn('updateUserRole failed', error);
                    }
                }
            }
        }
    }

    if (qtiContext) {
        yield put(setCurrentQtiContextItem(qtiContext.objectId, qtiContext, oldContext.currentQtiContextItem));
        yield put(setCurrentResourceContextItem(undefined));
    } else {
        const isXtmSectionContext = newContext.workspaceId === 'item_review_xtm' && isZeroOrPositiveInt(newContext.contextId);
        yield put(setCurrentQtiContextItem(undefined, undefined, oldContext?.currentQtiContextItem));
        if (newContext?.resourceContextItemId || newContext?.link) {
            if (oldContext.currentItemContext?.additionalResources) {
                const resourceHit = oldContext.currentItemContext.additionalResources.find(
                    (item: AdditionalResource) => (newContext.resourceContextItemId && item.contentItemId === newContext.resourceContextItemId)
                        || (newContext.link && item.link === newContext.link)
                );
                if (resourceHit && navigableResourceType.includes(resourceHit.type)) {
                    yield put(setCurrentResourceContextItem(resourceHit));
                }
            }
        } else {
            yield put(setCurrentResourceContextItem(undefined));
        }

        if (isXtmSectionContext && hasQuestionTestCasesAccess()) {
            // Skip re-resolving on unrelated LOCATION_CHANGE events to save network calls.
            const sectionOrQuestionChanged = oldContext.contextId !== newContext.contextId
                || oldContext.scorableItem !== newContext.scorableItem;
            if (sectionOrQuestionChanged || !oldContext.currentQtiContextItem) {
                yield fork(resolveXtmQuestionContext, newContext, oldContext);
            }
        }
    }

    if (itemBankInfo) {
        yield put(setItemBankInfo(newContext.itemBankId, itemBankInfo));
    }

    yield all([
        updateWorkspaceId(oldContext, newContext),
        updateItemBankId(oldContext, newContext),
        updateContentEditStatusForContext(oldContext, itemContext),
        updateIsIBTemplate(oldContext, newContext),
    ]);

    const workspaceScheme = yield workspaceSchemeLoadingService.fetchWorkspaceScheme(
        context?.currentItemContext?.isTemplate
        || itemContext?.currentItemContext?.isTemplate,
        isIBTemplate
    );
    yield put(workspaceSchemeLoaded(workspaceScheme));

    if (!isZeroOrPositiveInt(oldContext.contextId) && isZeroOrPositiveInt(newContext.contextId)) {
        yield call(urlContextParametersService.setContextId, newContext.contextId, true);
    }
}

function* checkLocationAndContext(locationChangeAction: LocationChangeAction<LocationState>) {
    if (locationChangeAction.payload.isFirstRendering) {
        const user = yield call(commonApiService.fetchLoggedInUser);
        const composedUser: UserObject = {
            ...user.user, privileges: user.user.roles, id: user.user.userId, permissions: user.permissions,
        };

        yield put(setUserInfo(composedUser));
        yield put(setMarketplaceProperties(window.marketplaceProperties));
        yield put(setupWorkspaces());
    }

    const oldContext: ContextState = yield select(getContextState);
    const routerState: RouterState = yield select(getRouterState);
    const newContext: ContextUrlSearchStringObject = routingService.getNewContextFromLocation(routerState.location);

    const getWorkspaceId = urlUtil.getWorkspaceIdFromPathname(routerState);
    if (getWorkspaceId) {
        newContext.workspaceId = getWorkspaceId;
    }

    newContext.workspaceId = resolveWorkspaceIdForCURview(newContext.workspaceId)!;

    yield call(updateContext, oldContext, newContext, !!locationChangeAction.payload.isFirstRendering);
}

function* unLockContext(action: UnLockContext) {
    const context: ContextState = yield select(getContextState);
    const { contextId, isLocked, qtiContextItemId } = context;
    if (!isLocked) {
        try {
            yield call(apiService.unlockItem, contextId);
            if (qtiContextItemId > 0) {
                yield call(apiService.unlockItem, qtiContextItemId);
            }
        } catch (e) {
            console.error('Failed UnLocking - ', contextId, e);
        }
    }
    const { navigateBackToLists } = action;
    if (navigateBackToLists) {
        navigationListService.backToOriginalLists(context);
    }
}

function getLandingWorkspaceByPriority(
    workspaceGroups: WorkspaceGroup[],
    priorityOrder: string[],
) {
    const allWorkspaces: WorkspaceItem[] = [];
    for (const group of workspaceGroups) {
        if (group.workspaces && group.workspaces.length > 0) {
            allWorkspaces.push(...group.workspaces);
        }
    }
    for (const ref of priorityOrder) {
        const workspace = allWorkspaces.find((ws: WorkspaceItem) => {
            if (!commonWorkspaceSchemeService.isInternalReferenceWorkspace(ws)) {
                return false;
            }

            return (
                (ws as InternalReferenceWorkspaceItem).internal_ref === ref
                && ws.visible
            );
        });
        if (workspace) {
            return workspace;
        }
    }
    return null;
}

function isWorkspaceVisible(ref: string, workspacesState: WorkspacesState) {
    const availableGroups = commonWorkspaceSchemeService.getAvailableWorkspacesForUser(workspacesState);
    for (const group of availableGroups) {
        const ws = group.workspaces.find((w: WorkspaceItem) => commonWorkspaceSchemeService.getWorkspaceIdentifier(w) === ref);
        if (ws) {
            return true;
        }
    }
    return false;
}

function getContributorPriority(
    sessionState: any,
    workspacesState: WorkspacesState
): string[] {
    const marketplaceProperties = sessionState?.marketplaceProperties || {};
    const hasQuestionTestCasesReviewAccess = hasQuestionTestCasesAccess(marketplaceProperties);
    const isItemReviewEnabled = marketplaceProperties.ENABLE_ITEM_REVIEW || hasQuestionTestCasesReviewAccess;
    const isXTMEnabled = marketplaceProperties.ENABLE_ITEM_REVIEW_XTM;
    const itemReviewRefXTM = isXTMEnabled ? 'item_review_xtm' : '';
    const itemReviewRefDefault = isItemReviewEnabled ? 'item_bank_default' : '';

    const contributorPriority: string[] = [];

    if (isXTMEnabled && isWorkspaceVisible(itemReviewRefXTM, workspacesState)) {
        contributorPriority.push(itemReviewRefXTM);
    }
    if (isItemReviewEnabled && isWorkspaceVisible(itemReviewRefDefault, workspacesState)) {
        contributorPriority.push(itemReviewRefDefault);
    }
    contributorPriority.push(
        'author_question_edit_only_with_editor',
        'author_question_edit_only',
        'author_edit',
        'author_preview',
    );
    return contributorPriority;
}

export function* loadWorkspaceScheme() {
    if (!permissionService.hasCurrentUserAccessForAuthorTool()) {
        return;
    }

    if (isQuestionSetSummaryWorkspace()) {
        // Standalone Question Set Summary iframe platform-ia: Don't load workspace scheme for this view, as it is not needed and will cause errors.
        return;
    }

    const contextState: ContextState = yield select(getContextState);
    const { isIBTemplate } = contextState;
    const workspaceScheme = yield workspaceSchemeLoadingService.fetchWorkspaceScheme(false, isIBTemplate);

    yield put(workspaceSchemeLoaded(workspaceScheme));
    const workspacesState: WorkspacesState = yield select(getWorkspaceState);

    const routerState: RouterState = yield select(getRouterState);
    let workspaceIdInUrl: string | undefined = urlUtil.getUrlParam('workspaceId') || urlUtil.getWorkspaceIdFromPathname(routerState);

    workspaceIdInUrl = resolveWorkspaceIdForCURview(workspaceIdInUrl);

    if (workspaceIdInUrl) {
        navigationListService.navigateToWorkspace(workspaceIdInUrl);
        return;
    }

    // Only pick a default landing workspace if none is selected
    const PRIORITY_MAPPING: Record<string, string[]> = {
        CUR: [
            'author_question_edit_only_with_editor',
            'author_question_edit_only',
            'author_edit',
            'author_preview',
            'item_review_xtm',
        ],
    };

    if (contextState.workspaceId || workspacesState.workspaceScheme.workspaceGroups.length === 0) {
        return;
    }

    // Check if current user is a Contributor based on session roles
    const sessionState = yield select(getSessionState);
    const user = sessionState?.user;
    const roles = user?.roles || [];
    const isContributor = roles.some((role: string) => /system_role_\d+_rel/.test(role));

    const privileges = user?.privileges || [];
    const customPrivileges = user?.customPrivileges || [];
    const hasEA = privileges?.includes('system_role_testextendedcontentadmin_rel');
    const hasAuthor = privileges?.includes('system_role_testauthor_rel');
    const hasCUR = customPrivileges?.length > 0;
    const isNewContributorFlow = (isContributor && hasCUR && !document?.referrer) && !hasEA && !hasAuthor;

    let selectedWorkspace: WorkspaceItem | null = null;

    if (isNewContributorFlow) {
        const contributorPriority = getContributorPriority(sessionState, workspacesState);
        selectedWorkspace = getLandingWorkspaceByPriority(
            workspacesState.workspaceScheme.workspaceGroups,
            contributorPriority,
        );
    } else {
        // Regular users: use CUR priority mapping
        selectedWorkspace = getLandingWorkspaceByPriority(
            workspacesState.workspaceScheme.workspaceGroups,
            PRIORITY_MAPPING.CUR,
        );
    }

    // Final fallback if no prioritized workspace matches
    if (!selectedWorkspace) {
        selectedWorkspace = commonWorkspaceSchemeService.getFirstVisibleWorkspace(
            workspacesState.workspaceScheme.workspaceGroups,
        );
    }

    if (selectedWorkspace) {
        const firstWorkspaceId = commonWorkspaceSchemeService.getWorkspaceIdentifier(selectedWorkspace);
        const resolvedWorkspaceId = resolveWorkspaceIdForCURview(firstWorkspaceId);
        navigationListService.navigateToWorkspace(resolvedWorkspaceId ?? firstWorkspaceId);
    } else {
        const alert: Alert = {
            key: 'common.workspace.all.notAvailable',
            info: 'common.workspace.all.notAvailable.description',
            level: AlertLevel.ERROR,
            payload: {},
        };
        yield put(pushAlert(alert));
    }
}

function* handleReadOnlyContentEditStatus(contentEditStatusResponse: any, objectId: number, skipLockModal?: boolean) {
    yield put(setCurrentItemContextLock(true));

    if (contentEditStatusResponse?.lockInfo && !skipLockModal) {
        yield put(showContextLockModal(objectId));
    }
}

function* handleEditableContentEditStatus(objectId: number) {
    yield put(setCurrentItemContextLock(false));

    try {
        const hasEditAccess = hasCURAccessForAction(FeatureTypesWithCurs.CONTENT_BLOCK_EDIT);
        if (hasEditAccess) {
            yield call(apiService.lockItem, objectId);
        }
    } catch (e) {
        console.error('Failed Locking - ', objectId, e);
    }
}

function* showTrashedContentAlertIfNeeded(contentEditStatusResponse: any) {
    if (contentEditStatusResponse.isReadOnlyContent && contentEditStatusResponse.reasonOfReadOnly === 'is-trashed') {
        const alert: Alert = {
            key: 'author.contentEdit.key.isTrashed',
            info: 'author.contentEdit.info.isTrashed',
            level: AlertLevel.INFO,
            link: { text: tl('author.contentEdit.restoreNow'), action: restoreCurrentQtiContextItem() },
        };
        yield put(pushAlert(alert));
    }
}

function* updateContentEditStatusForContext(oldContext: ContextState, newContext: ContextState, skipLockModal?: boolean) {
    if (newAuthorUIService.isNewAuthorUIEnabled() && oldContext?.contextId === newContext?.objectId) {
        return;
    }
    if (oldContext?.objectId === newContext?.objectId) {
        return;
    }

    const { objectId } = newContext;
    const liveContentRevisionId = oldContext.liveContentRevisionId || newContext.liveContentRevisionId;

    try {
        const contentEditStatusResponse = yield call(apiService.fetchContentEditStatus, liveContentRevisionId);
        yield put(setContextEditStatus(oldContext.objectId, contentEditStatusResponse));

        if (contentEditStatusResponse?.isReadOnlyContent) {
            yield handleReadOnlyContentEditStatus(contentEditStatusResponse, objectId, skipLockModal);
        } else {
            yield handleEditableContentEditStatus(objectId);
        }

        yield showTrashedContentAlertIfNeeded(contentEditStatusResponse);
    } catch (e) {
        yield put(resetContextEditStatus(objectId));
        console.error('Failed getting ContentEditStatus for ', liveContentRevisionId, e);
    }
}

export function* updateCurrentItemContext(actionData?: any) {
    const skipLockModal = actionData?.payload;
    const contextState: ContextState = yield select(getContextState);
    const { isIBTemplate } = contextState;
    const itemContext = yield call(
        listLiveDataObjectLoaderService.getContextDataObject,
        ITEM_INDEX,
        contextState.contextId,
        -1,
        contextState.itemBankId
    );

    if (itemContext) {
        yield put(setCurrentItemContext(itemContext.objectId, itemContext));
        yield updateContentEditStatusForContext(contextState, itemContext, skipLockModal);
    }
    yield put(workspaceSchemeLoaded(
        yield workspaceSchemeLoadingService.fetchWorkspaceScheme(contextState.currentItemContext.isTemplate, isIBTemplate)
    ));
}

function* updateCurrentQtiContextItem() {
    const contextState: ContextState = yield select(getContextState);
    const qtiContext = yield call(
        listLiveDataObjectLoaderService.getContextDataObject, QTI_CONTEXT_INDEX, contextState.contextId, contextState.qtiContextItemId
    );
    if (qtiContext) {
        yield put(setCurrentQtiContextItem(qtiContext.objectId, qtiContext, qtiContext.objectId));
    }
}

export function* restoreCurrentQtiSection() {
    const contextState: ContextState = yield select(getContextState);
    const restoreItem = yield call(apiService.restoreItem, contextState.contentItemIds, 'visible');

    if (restoreItem.pushChannel) {
        const alert: Alert = {
            key: 'author.contentEdit.key.isRestored',
            info: 'author.contentEdit.info.isRestored',
            level: AlertLevel.SUCCESS,
        };
        yield put(pushAlert(alert));
        window.location.reload();
    }
}

function* renameContextHandler(action: RenameContextItem) {
    const { contextId } = yield select(getContextState);
    if(!action.title){
        const alert: Alert = {
            key: 'author.contentEdit.key.titleMissingWarning',
            info: 'author.contentEdit.info.titleMissingWarning',
            level: AlertLevel.WARNING,
        };
        yield put(pushAlert(alert));
        return;
    }
    const response = yield safe(
        contextId, call(apiService.renameContextItem, contextId, action.title),
        'RenameContextItem'
    );

    if (response.success) {
        if (newAuthorUIService.isNewAuthorUIEnabled()) {
            storeManager.dispatch(showNotification(
                `${newAuthorUIService.contextName()} successfully renamed to ${action.title}.`,
                'Success'
            ));
        }
        const topic = awsIOTService.buildTopic(response.pushChannel_itemsIndex_context);

        const renameIndexCallback = (message: Paho.Message) => {
            if (message) {
                storeManager.dispatch(updateItemContext());
            }
        };

        yield call(awsIOTService.subscribeToTopic, topic, renameIndexCallback.bind(awsIOTService));
    }
}

const isStimulus = (page: any) => {
    return page.category === categories.STIMULUS;
};

const getQuestionsWithoutStimulus = (section: any) => {
    return section?.items?.filter((page: any) => {
        return !isStimulus(page);
    });
};

const supportsSelection = (section: any) => {
    if (mandatoryQuestion(section)) {
        return false;
    }

    const questions = getQuestionsWithoutStimulus(section) ?? [];
    let previousMaxScore: any;

    for (let i = 0, len = questions.length; i < len; i++) {
        const question = questions[i];
        const maxScore = question?.resource?.maxScore;

        if (previousMaxScore !== undefined && previousMaxScore !== maxScore) {
            return false;
        }
        if (question?.resource?.type === qtiTypes.COMPOSITE_FORM
            || question?.resource?.type === ContentTypes.DOCUMENT) {
            return false;
        }
        previousMaxScore = maxScore;
    }

    return questions.length > 0;
};

const mandatoryQuestion = (section: any) => {
    return section?.items?.some((page: any) => {
        if (!page?.resource) return false;
        return page.resource.isMandatoryQuestion || page.resource.isMandatoryQuestion === 'true';
    });
};

const isSelectionDisabled = (section: any) => {
    const numberOfQuestionsToPull = section.numberOfQuestionsToPull ?? 0;
    // this indicates that selection was wanted by the user but sectionQuestionOrdering was set to none
    // as supportsSelection returned false in toJSON. numberOfQuestionsToPull of persisted on the server
    // to restore the wanted user settings if the user does changes to fulfill the selection criteria
    return !supportsSelection(section) && numberOfQuestionsToPull > 0;
};

// This logic is copied from legacy to update pulling / selection
// Update when there is a mandatory question in the section and selection is disabled.
function* sectionPullingCheck(assessmentData: any) {
    const sections = assessmentData?.sections ?? [];

    let hasDisabledSelection = false;
    let showMandatoryWarning = false;
    let hasMandatoryQuestion = false;
    let sectionIdForPullingDisabled: string | number | null = null;
    for (let i = 0, len = sections.length; i < len; i++) {
        const section = sections[i];
        hasMandatoryQuestion = mandatoryQuestion(section);
        if (isSelectionDisabled(section) && hasMandatoryQuestion) {
            showMandatoryWarning = true;
            sectionIdForPullingDisabled = section.id;
            break;
        } else if (isSelectionDisabled(section)) {
            hasDisabledSelection = true;
            sectionIdForPullingDisabled = section.id;
            break;
        }
    }
    if (hasDisabledSelection || showMandatoryWarning) {
        const context: ContextState = yield select(getContextState);
        const hasEditAccess = hasCURAccessForAction(FeatureTypesWithCurs.CONTENT_BLOCK_EDIT);
        const isReadOnly = context?.contentEditStatus?.isReadOnlyMetadata;

        if (isReadOnly || !hasEditAccess) {
            return;
        }

        const alert: Alert = {
            key: 'author.contentEdit.sectionPulling.saveContent.title',
            info: showMandatoryWarning
                ? 'views.editQuestionView.sectionPullingDisabledForMandatory'
                : 'views.editQuestionView.sectionPullingDisabled',
            level: AlertLevel.ERROR,
            payload: {},
        };

        if (sectionIdForPullingDisabled !== null && assessmentData?.contentItemId) {
            const updatedAssessmentData = JSON.parse(JSON.stringify(assessmentData));
            const section = updatedAssessmentData.sections.find(
                (section: any) => section.id === sectionIdForPullingDisabled
            );
            if (section && section.sectionQuestionOrdering !== QUESTION_DELIVERY_TYPE.NO_RANDOMIZATION) {
                section.sectionQuestionOrdering = QUESTION_DELIVERY_TYPE.NO_RANDOMIZATION;
                const dataToUpdate = {
                    id: assessmentData.contentItemId,
                    lastModified: assessmentData?.lastModified,
                    context: 'author',
                    // eslint-disable-next-line camelcase
                    status: assessmentData?.assessment_status,
                    jsonString: JSON.stringify(updatedAssessmentData),
                };

                // Updating the assessment to set sectionQuestionOrdering to 'none'
                try {
                    const response = yield call(
                        apiService.updateAssessmentData,
                        String(assessmentData.contentItemId),
                        dataToUpdate,
                    );
                    if (response) {
                        yield put(pushAlert(alert));
                        yield put(updateAssessmentSuccess());
                        yield put(getAssessment(String(assessmentData?.contentItemId)));
                    }
                } catch (err) {
                    console.error('sectionPullingCheck update assessment failed', err);
                }
            }
        }
    }
}

function* fetchAssessment(action: GetAssessmentAction) {
    try {
        const response = yield call(apiService.getAssessmentData, action.assessmentId);
        if (response) {
            yield put(getAssessmentSuccess(response));

            if(newAuthorUIService.isNewAuthorUIEnabled()){
                yield call(sectionPullingCheck, response);
            }
        }
    } catch (err) {
        console.error(`${action} failed`, err);
    }
}

function* fetchAssessmentDetails() {
    try {
        const contextId: number = yield select((state: RootState) => state.context.contextId);
        if (!contextId || contextId === -1) return;

        const response = yield call(apiService.fetchAssessmentDetails, contextId?.toString());
        if (response) {
            yield put(getAssessmentDetailsSuccess(response));
        }
    } catch (err) {
        console.error('fetchAssessmentDetails failed', err);
    }
}

function* updateAssessment(action: UpdateAssessmentAction) {
    try {
        const response = yield call(
            apiService.updateAssessmentData,
            action.assessmentId,
            action.assessmentData
        );

        if (response) {
            if (action.onSuccessCallback) {
                action.onSuccessCallback();
            }
            yield put(updateAssessmentSuccess());
            yield put(getAssessment(action.assessmentId));
            yield put(closeWorkspaceOverlay());
        }
    } catch (err) {
        console.error(`${action} failed`, err);
    }
}

export function getContentItemIdsNeedingUpdate(assessmentData: any): number[] {
    const contentItemIds: number[] = [];
    if (!assessmentData?.sections) return contentItemIds;

    for (const section of assessmentData.sections) {
        if (section?.items) {
            for (const item of section.items) {
                if (
                    item?.resource?.liveRevisionId
                    && item.resource.revisionId
                    && item.resource.liveRevisionId !== item.resource.revisionId
                ) {
                    contentItemIds.push(item.id);
                }
            }
        }
    }
    return contentItemIds;
}

export function* updateSingleContentToLiveRevision(contextId: number, contentItemId: number) {
    const response = yield call(
        apiService.updateContentInContextToLiveRevision,
        contextId,
        contentItemId,
    );
    return response;
}

export function* subscribeToUpdatePushChannels(response: UpdateContentInBulkResponse, featureId: string) {
    if (response.pushChannel_itemsIndex_context) {
        const topic = awsIOTService.buildTopic(response.pushChannel_itemsIndex_context);
        const itemsIndexCallback = (message: Paho.Message) => {
            if (message) {
                storeManager.dispatch(updateItemContext());
            }
        };
        yield call(awsIOTService.subscribeToTopic, topic, itemsIndexCallback.bind(awsIOTService));
    }
    if (response.pushChannel_contextIndex_context) {
        const topic = awsIOTService.buildTopic(response.pushChannel_contextIndex_context);
        const contextIndexCallback = (message: Paho.Message) => {
            if (message) {
                storeManager.dispatch(updateQtiContextItem());
                storeManager.dispatch(fetchStructureEditorLiveData(featureId));
            }
        };
        yield call(awsIOTService.subscribeToTopic, topic, contextIndexCallback.bind(awsIOTService));
    }
}

export function* bulkUpdateAssessment(action: BulkUpdateAssessmentAction) {
    try {
        const contextId: number = yield select((state: RootState) => state.context.contextId);
        const contentItemIds = getContentItemIdsNeedingUpdate(action.assessmentData);

        if (contentItemIds.length === 0) {
            yield put(getAssessment(action.assessmentId));
            return;
        }

        const successfulResponses: UpdateContentInBulkResponse[] = [];
        // Run sequentially to avoid backend conflicts and capture per-item failures.
        for (const contentItemId of contentItemIds) {
            try {
                const response = yield call(updateSingleContentToLiveRevision, contextId, contentItemId);
                if (response?.success) {
                    successfulResponses.push(response);
                } else {
                    console.error('Bulk update content item failed', { contentItemId, response });
                }
            } catch (err) {
                console.error('Bulk update content item threw', { contentItemId, err });
            }
        }

        if (successfulResponses.length > 0) {
            yield all(
                successfulResponses.map((response: UpdateContentInBulkResponse) => call(subscribeToUpdatePushChannels, response, action.featureId))
            );
            yield put(getAssessment(action.assessmentId));
            yield put(showBulkUpdateSuccessNotification(action.numberOfQuestionsNeedingUpdate));
        }
    } catch (err) {
        console.error('Bulk update assessment failed', err);
    }
}

function* addContentBlockSelection(action: AddContentBlockSelectionAction) {
    try {
        const response = yield call(
            apiService.addContentBlockSelectionData,
            action.assessmentId,
            action.payload
        );

        if (response) {
            yield put(addContentBlockSelectionSuccess());
            yield put(getAssessment(action.assessmentId));
            yield put(closeWorkspaceOverlay());
        }
    } catch (err) {
        console.error(`${JSON.stringify(action)} failed`, err);
    }
}

function* watchWorkspacesContextChanged() {
    yield takeLatest(ContextTypeKeys.SETUP_WORKSPACES, loadWorkspaceScheme);
}

function* watchUpdateCurrentItemContext() {
    yield takeLatest(ContextTypeKeys.UPDATE_CURRENT_ITEM_CONTEXT, updateCurrentItemContext);
}

function* watchUpdateCurrentQtiContextItem() {
    yield takeLatest(ContextTypeKeys.UPDATE_CURRENT_QTI_CONTEXT_ITEM, updateCurrentQtiContextItem);
}
function* watchRestoreCurrentQtiContextItem() {
    yield takeLatest(ContextTypeKeys.RESTORE_CURRENT_QTI_CONTEXT_ITEM, restoreCurrentQtiSection);
}

function* watchRouterLocationChange() {
    yield takeLatest(LOCATION_CHANGE, checkLocationAndContext);
}

function* watchUnlockContext() {
    yield takeLatest(ContextTypeKeys.UNLOCK_CONTEXT, unLockContext);
}

function* watchContextRename() {
    yield takeLatest(ContextTypeKeys.CONTEXT_RENAME, renameContextHandler);
}

function* watchFetchAdditionalContextItemData() {
    yield takeLatest(ContextTypeKeys.FETCH_ADDITIONAL_CONTEXT_ITEM_DATA, fetchContextQtiData);
}

function* watchFetchContextItemBankInfo() {
    yield takeLatest(ContextTypeKeys.FETCH_CONTEXT_ITEM_BANK_INFO, fetchContextItemBankInfoData);
}

function* watchAssessmentRequest() {
    yield takeLatest(ContextTypeKeys.GET_ASSESSMENT, fetchAssessment);
}

function* watchAssessmentDetailsRequest() {
    yield takeLatest(ContextTypeKeys.GET_ASSESSMENT_DETAILS, fetchAssessmentDetails);
}

function* watchAssessmentUpdate() {
    yield takeLatest(ContextTypeKeys.UPDATE_ASSESSMENT, updateAssessment);
}

function* watchBulkAssessmentUpdate() {
    yield takeLatest(ContextTypeKeys.BULK_UPDATE_ASSESSMENT, bulkUpdateAssessment);
}

function* watchAddContentBlockSelection() {
    yield takeLatest(ContextTypeKeys.ADD_CONTENT_BLOCK_SELECTION, addContentBlockSelection);
}

export function* rootContextSaga() {
    yield all([
        watchRouterLocationChange(),
        watchFetchAdditionalContextItemData(),
        watchUpdateCurrentItemContext(),
        watchUpdateCurrentQtiContextItem(),
        watchWorkspacesContextChanged(),
        watchUnlockContext(),
        watchRestoreCurrentQtiContextItem(),
        watchFetchContextItemBankInfo(),
        watchContextRename(),
        watchAssessmentRequest(),
        watchAssessmentUpdate(),
        watchQuestionSetSupportedLanguages(),
        watchBulkAssessmentUpdate(),
        watchAssessmentDetailsRequest(),
        watchAddContentBlockSelection(),
    ]);
}
